import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import ControlsHeader from "shared/ControlsHeader";
import SearchBar from "shared/SearchBar";
import StatsSummaryCards from "DreamCast/Components/StatsSummaryCards";
import CustomerActivityGraph from "DreamCast/Components/ConsumerActivityGraph";
import StatsPieChart from "DreamCast/Components/CategoryBreakdownPieChart";
import StatsPerCategorySection from "DreamCast/Components/StatsPerCategorySection";
import TrendingSection from "DreamCast/Components/TrendingSection";
import styles from "./Statistics.module.css";

const WEEKDAY_OPTIONS = [
  { day: "Mon", label: "Monday" },
  { day: "Tue", label: "Tuesday" },
  { day: "Wed", label: "Wednesday" },
  { day: "Thu", label: "Thursday" },
  { day: "Fri", label: "Friday" },
  { day: "Sat", label: "Saturday" },
  { day: "Sun", label: "Sunday" },
];

const DEFAULT_GENERAL_METRICS = [
  { title: "Total Plays", total: "198,430" },
  { title: "Playlists Created", total: "12,842" },
  { title: "Hours Listened", total: "56,980" },
  { title: "Audios Shared", total: "22,417" },
];

const DEFAULT_CONSUMER_ACTIVITY = [
  { day: "Mon", value: 32000 },
  { day: "Tue", value: 41000 },
  { day: "Wed", value: 36500 },
  { day: "Thu", value: 49000 },
  { day: "Fri", value: 44000 },
  { day: "Sat", value: 30000 },
  { day: "Sun", value: 26500 },
];

const DEFAULT_GENERAL_BREAKDOWN = [
  { label: "Audios", value: 84000 },
  { label: "Videos", value: 52000 },
  { label: "Playlists", value: 38000 },
  { label: "Radio", value: 24000 },
];

const DEFAULT_CATEGORY_DATA = {
  Audios: {
    barData: {
      "This Week": [
        { label: "Sermons", value: 43000 },
        { label: "Worship", value: 38000 },
        { label: "Teaching", value: 50000 },
        { label: "Podcasts", value: 32000 },
        { label: "Stories", value: 27000 },
      ],
      "This Month": [
        { label: "Sermons", value: 180000 },
        { label: "Worship", value: 162000 },
        { label: "Teaching", value: 210000 },
        { label: "Podcasts", value: 134000 },
        { label: "Stories", value: 119000 },
      ],
      "This Year": [
        { label: "Sermons", value: 1920000 },
        { label: "Worship", value: 1740000 },
        { label: "Teaching", value: 2200000 },
        { label: "Podcasts", value: 1450000 },
        { label: "Stories", value: 1230000 },
      ],
    },
    pie1Data: [
      { label: "Completed", value: 4200 },
      { label: "In Progress", value: 2800 },
      { label: "Queued", value: 1500 },
      { label: "Skipped", value: 900 },
    ],
    pie2Data: [
      { label: "Mobile", value: 5300 },
      { label: "Desktop", value: 3200 },
      { label: "Tablet", value: 900 },
      { label: "Other", value: 400 },
    ],
  },
  Videos: {
    barData: {
      "This Week": [
        { label: "Sermons", value: 28000 },
        { label: "Worship", value: 22000 },
        { label: "Teaching", value: 35000 },
        { label: "Events", value: 19000 },
        { label: "Shorts", value: 16000 },
      ],
      "This Month": [
        { label: "Sermons", value: 115000 },
        { label: "Worship", value: 94000 },
        { label: "Teaching", value: 142000 },
        { label: "Events", value: 78000 },
        { label: "Shorts", value: 67000 },
      ],
      "This Year": [
        { label: "Sermons", value: 1240000 },
        { label: "Worship", value: 1010000 },
        { label: "Teaching", value: 1530000 },
        { label: "Events", value: 840000 },
        { label: "Shorts", value: 720000 },
      ],
    },
    pie1Data: [
      { label: "Completed", value: 3100 },
      { label: "In Progress", value: 2100 },
      { label: "Queued", value: 900 },
      { label: "Skipped", value: 600 },
    ],
    pie2Data: [
      { label: "Mobile", value: 3900 },
      { label: "Desktop", value: 2800 },
      { label: "Tablet", value: 700 },
      { label: "Other", value: 200 },
    ],
  },
  Playlists: {
    barData: {
      "This Week": [
        { label: "Morning", value: 15000 },
        { label: "Evening", value: 12000 },
        { label: "Workout", value: 18000 },
        { label: "Focus", value: 9000 },
        { label: "Relax", value: 7000 },
      ],
      "This Month": [
        { label: "Morning", value: 62000 },
        { label: "Evening", value: 49000 },
        { label: "Workout", value: 75000 },
        { label: "Focus", value: 37000 },
        { label: "Relax", value: 29000 },
      ],
      "This Year": [
        { label: "Morning", value: 660000 },
        { label: "Evening", value: 530000 },
        { label: "Workout", value: 810000 },
        { label: "Focus", value: 400000 },
        { label: "Relax", value: 310000 },
      ],
    },
    pie1Data: [
      { label: "Created", value: 1800 },
      { label: "Active", value: 1200 },
      { label: "Archived", value: 400 },
      { label: "Shared", value: 300 },
    ],
    pie2Data: [
      { label: "Mobile", value: 2100 },
      { label: "Desktop", value: 1800 },
      { label: "Tablet", value: 400 },
      { label: "Other", value: 100 },
    ],
  },
  Radio: {
    barData: {
      "This Week": [
        { label: "Station 1", value: 9100 },
        { label: "Station 2", value: 8200 },
        { label: "Station 3", value: 7600 },
        { label: "Station 4", value: 6400 },
        { label: "Station 5", value: 5100 },
      ],
      "This Month": [
        { label: "Station 1", value: 38000 },
        { label: "Station 2", value: 34000 },
        { label: "Station 3", value: 31000 },
        { label: "Station 4", value: 26000 },
        { label: "Station 5", value: 21000 },
      ],
      "This Year": [
        { label: "Station 1", value: 410000 },
        { label: "Station 2", value: 370000 },
        { label: "Station 3", value: 335000 },
        { label: "Station 4", value: 280000 },
        { label: "Station 5", value: 225000 },
      ],
    },
    pie1Data: [
      { label: "Listened", value: 2200 },
      { label: "Saved", value: 800 },
      { label: "Skipped", value: 600 },
      { label: "Queued", value: 200 },
    ],
    pie2Data: [
      { label: "Mobile", value: 2500 },
      { label: "Desktop", value: 900 },
      { label: "Tablet", value: 300 },
      { label: "Other", value: 100 },
    ],
  },
};

const DEFAULT_TRENDING_SPEAKERS = {
  "This Week": [
    { name: "Micah Tyler", listens: 43000 },
    { name: "Leah West", listens: 50000 },
    { name: "Jun Park", listens: 37000 },
    { name: "Nora Reed", listens: 32000 },
    { name: "Caleb Yoon", listens: 28000 },
  ],
  "This Month": [
    { name: "Micah Tyler", listens: 180000 },
    { name: "Leah West", listens: 194000 },
    { name: "Jun Park", listens: 161000 },
    { name: "Nora Reed", listens: 149000 },
    { name: "Caleb Yoon", listens: 133000 },
  ],
  "This Year": [
    { name: "Micah Tyler", listens: 1890000 },
    { name: "Leah West", listens: 2010000 },
    { name: "Jun Park", listens: 1760000 },
    { name: "Nora Reed", listens: 1550000 },
    { name: "Caleb Yoon", listens: 1410000 },
  ],
};

const DEFAULT_TRENDING_CONTENT = {
  "This Week": [
    { title: "Grace Enough", listens: 47000 },
    { title: "The Way Forward", listens: 41000 },
    { title: "Still Waters", listens: 36000 },
    { title: "Morning Light", listens: 29000 },
    { title: "Firm Foundation", listens: 24000 },
  ],
  "This Month": [
    { title: "Grace Enough", listens: 197000 },
    { title: "The Way Forward", listens: 175000 },
    { title: "Still Waters", listens: 152000 },
    { title: "Morning Light", listens: 123000 },
    { title: "Firm Foundation", listens: 101000 },
  ],
  "This Year": [
    { title: "Grace Enough", listens: 2100000 },
    { title: "The Way Forward", listens: 1880000 },
    { title: "Still Waters", listens: 1640000 },
    { title: "Morning Light", listens: 1320000 },
    { title: "Firm Foundation", listens: 1090000 },
  ],
};

function resolveWeekdayLabel(dayValue) {
  const found = WEEKDAY_OPTIONS.find((option) => option.day === dayValue);
  return found ? found.label : "Thursday";
}

export function Statistics({
  headerTitle,
  summaryMetrics,
  consumerActivityData,
  generalCategoryBreakdown,
  categoryData,
  trendingSpeakersByTimeframe,
  trendingContentByTimeframe,
}) {
  const [activeCategory, setActiveCategory] = useState("Audios");
  const [activeDay, setActiveDay] = useState("Thu");
  const [speakersTimeframe, setSpeakersTimeframe] = useState("This Week");
  const [contentTimeframe, setContentTimeframe] = useState("This Week");
  const [searchQuery, setSearchQuery] = useState("");

  const dropdownLabel = useMemo(
    () => resolveWeekdayLabel(activeDay),
    [activeDay],
  );

  // Determine if search query matches any visible statistic item
  const hasSearchMatch = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    if (!q) return true;

    // summary metrics titles
    if (
      Array.isArray(summaryMetrics) &&
      summaryMetrics.some((m) => String(m.title || "").toLowerCase().includes(q))
    ) {
      return true;
    }

    // general category breakdown labels
    if (
      Array.isArray(generalCategoryBreakdown) &&
      generalCategoryBreakdown.some((g) => String(g.label || "").toLowerCase().includes(q))
    ) {
      return true;
    }

    // trending speakers across all timeframes
    if (
      trendingSpeakersByTimeframe &&
      Object.values(trendingSpeakersByTimeframe).some((arr) =>
        Array.isArray(arr) && arr.some((s) => String(s.name || "").toLowerCase().includes(q)),
      )
    ) {
      return true;
    }

    // trending content across all timeframes
    if (
      trendingContentByTimeframe &&
      Object.values(trendingContentByTimeframe).some((arr) =>
        Array.isArray(arr) && arr.some((c) => String(c.title || "").toLowerCase().includes(q)),
      )
    ) {
      return true;
    }

    // category names
    if (
      categoryData &&
      Object.keys(categoryData).some((k) => String(k || "").toLowerCase().includes(q))
    ) {
      return true;
    }

    return false;
  }, [
    searchQuery,
    summaryMetrics,
    generalCategoryBreakdown,
    trendingSpeakersByTimeframe,
    trendingContentByTimeframe,
    categoryData,
  ]);

  const noResults = !hasSearchMatch && String(searchQuery || "").trim() !== "";

  return (
    <div className={styles.page}>
      <ControlsHeader title={headerTitle} />

      <div className={styles.content}>
        <div className={styles.topSearchRow}>
          <div className={styles.searchBarWrap}>
            <SearchBar
              placeholderText="Search Statistics"
              value={searchQuery}
              onInput={setSearchQuery}
              noResults={noResults}
              minChar={0}
              searchDelay={0}
            />
          </div>
        </div>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.headerLine} />
            <h2 className={styles.headerTitle}>General</h2>
            <span className={styles.headerLine} />
          </div>

          <div className={styles.summaryRow}>
            {summaryMetrics.map((item) => (
              <StatsSummaryCards
                key={item.title}
                title={item.title}
                total={item.total}
              />
            ))}
          </div>

          <div className={styles.generalChartsRow}>
            <CustomerActivityGraph
              title="Consumer Activity"
              data={consumerActivityData}
              activeDay={activeDay}
              dropdownLabel={dropdownLabel}
              dropdownOptions={WEEKDAY_OPTIONS.map((option) => option.label)}
              onDropdownChange={(nextLabel) => {
                const selected = WEEKDAY_OPTIONS.find(
                  (option) => option.label === nextLabel,
                );
                setActiveDay(selected ? selected.day : "Thu");
              }}
            />

            <StatsPieChart data={generalCategoryBreakdown} />
          </div>
        </section>

        <StatsPerCategorySection
          activeCategory={activeCategory}
          data={categoryData}
          onCategoryChange={setActiveCategory}
        />

        <TrendingSection
          speakersData={trendingSpeakersByTimeframe[speakersTimeframe]}
          contentData={trendingContentByTimeframe[contentTimeframe]}
          speakersTimeframe={speakersTimeframe}
          contentTimeframe={contentTimeframe}
          onSpeakersTimeframeChange={setSpeakersTimeframe}
          onContentTimeframeChange={setContentTimeframe}
        />
      </div>
    </div>
  );
}

Statistics.propTypes = {
  headerTitle: PropTypes.string,
  summaryMetrics: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      total: PropTypes.string.isRequired,
    }),
  ),
  consumerActivityData: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ),
  generalCategoryBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ),
  categoryData: PropTypes.shape({
    Audios: PropTypes.object,
    Videos: PropTypes.object,
    Playlists: PropTypes.object,
    Radio: PropTypes.object,
  }),
  trendingSpeakersByTimeframe: PropTypes.shape({
    "This Week": PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        listens: PropTypes.number.isRequired,
      }),
    ),
    "This Month": PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        listens: PropTypes.number.isRequired,
      }),
    ),
    "This Year": PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        listens: PropTypes.number.isRequired,
      }),
    ),
  }),
  trendingContentByTimeframe: PropTypes.shape({
    "This Week": PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        listens: PropTypes.number.isRequired,
      }),
    ),
    "This Month": PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        listens: PropTypes.number.isRequired,
      }),
    ),
    "This Year": PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        listens: PropTypes.number.isRequired,
      }),
    ),
  }),
};

Statistics.defaultProps = {
  headerTitle: "DreamCast - Statistics",
  summaryMetrics: DEFAULT_GENERAL_METRICS,
  consumerActivityData: DEFAULT_CONSUMER_ACTIVITY,
  generalCategoryBreakdown: DEFAULT_GENERAL_BREAKDOWN,
  categoryData: DEFAULT_CATEGORY_DATA,
  trendingSpeakersByTimeframe: DEFAULT_TRENDING_SPEAKERS,
  trendingContentByTimeframe: DEFAULT_TRENDING_CONTENT,
};

export default Statistics;
